import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';

import { TContextGroup, TContextGroupCreate, TContextGroupUpdate } from '@core/api/context/types';
import { TCountry } from '@core/api/countries/types';
import { ContextService } from '@shared/services/context.service';
import { CountriesService } from '@shared/services/countries.service';

export interface ContextGroupFormModalData {
  group?: TContextGroup;
  parentGroup?: TContextGroup;
  isEdit?: boolean;
}

@Component({
  selector: 'app-context-group-form-modal',
  templateUrl: './context-group-form-modal.component.html',
  styleUrls: ['./context-group-form-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
})
export class ContextGroupFormModalComponent implements OnInit, OnDestroy {
  @Output() groupSaved = new EventEmitter<TContextGroup>();

  form: FormGroup;
  isEdit = false;
  parentGroupName: string | null = null;
  loading = false;

  // Available options
  availableCountries: TCountry[] = [];

  // Priority options (1=highest, 5=lowest)
  priorityOptions = [
    { value: 1, label: '1 - Highest' },
    { value: 2, label: '2 - High' },
    { value: 3, label: '3 - Medium' },
    { value: 4, label: '4 - Low' },
    { value: 5, label: '5 - Lowest' },
  ];

  private _subs: Subscription[] = [];

  constructor(
    private _fb: FormBuilder,
    private _contextService: ContextService,
    private _countriesService: CountriesService,
    private _dialogRef: MatDialogRef<ContextGroupFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContextGroupFormModalData
  ) {
    this.form = this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      instruction: ['', [Validators.maxLength(2000)]],
      descriptionForTools: ['', [Validators.maxLength(500)]],
      countries: [[] as string[]],
      priority: [null],
      status: ['active'],
      approved: [false],
    });
  }

  ngOnInit(): void {
    this.isEdit = !!this.data?.isEdit;

    // Load countries
    this._countriesService.getCountries();
    this._subs.push(
      this._countriesService.getCountriesObservable().subscribe(countries => {
        this.availableCountries = countries;
      })
    );

    if (this.data?.parentGroup) {
      this.parentGroupName = this.data.parentGroup.name;
    }

    if (this.data?.group && this.isEdit) {
      // Extract country IDs (backend populates as objects)
      const countryIds = (this.data.group.countries || []).map((c: any) =>
        typeof c === 'string' ? c : c._id
      );

      this.form.patchValue({
        name: this.data.group.name,
        instruction: this.data.group.instruction || '',
        descriptionForTools: this.data.group.descriptionForTools || '',
        countries: countryIds,
        priority: this.data.group.priority || null,
        status: this.data.group.status || 'active',
        approved: this.data.group.approved || false,
      });
    }
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = this.form.value;

    if (this.isEdit && this.data?.group) {
      const updateData: TContextGroupUpdate = {
        name: formValue.name,
        instruction: formValue.instruction || undefined,
        descriptionForTools: formValue.descriptionForTools || undefined,
        countries: formValue.countries || [],
        priority: formValue.priority || undefined,
        status: formValue.status,
        approved: formValue.approved,
      };

      this._contextService.updateGroup(this.data.group._id, updateData, updated => {
        this.loading = false;
        this.groupSaved.emit(updated);
      });
    } else {
      const createData: TContextGroupCreate = {
        name: formValue.name,
        instruction: formValue.instruction || undefined,
        descriptionForTools: formValue.descriptionForTools || undefined,
        countries: formValue.countries || [],
        priority: formValue.priority || undefined,
        status: formValue.status,
        approved: formValue.approved,
        parentGroup: this.data?.parentGroup?._id,
      };

      this._contextService.createGroup(createData, created => {
        this.loading = false;
        this.groupSaved.emit(created);
      });
    }
  }

  onCancel(): void {
    this._dialogRef.close();
  }

  get title(): string {
    if (this.isEdit) {
      return 'Edit Context Group';
    }
    return this.parentGroupName ? `Create Child Group under "${this.parentGroupName}"` : 'Create Context Group';
  }
}


